import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCopy, IconScan } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../../image/compress/compressImageEngine';
import ImageDropZone from '../../components/image/ImageDropZone';
import useImagePaste from '../../components/image/useImagePaste';
import { ImageFileRow } from '../../components/image/ImageFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import AiToolLink from '../../components/common/AiToolLink';
import ToolBoothTabs from '../../components/common/ToolBoothTabs';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    LANG_OPTIONS,
    MAX_FILE_BYTES,
    extractTextFromImage,
    isSupportedImageToTextFile,
} from './imageToTextEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/bmp,image/gif,.jpg,.jpeg,.png,.webp,.bmp,.gif';

const textareaClass = 'min-h-[min(420px,55vh)] w-full resize-y rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

function langButtonClass(active) {
    return `rounded-lg border px-3 py-2 text-[12.5px] font-semibold transition-colors ${
        active
            ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-300'
            : 'border-[#ECE8E4] text-[#6B635C] hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300'
    }`;
}

export default function ImageToText() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const previewUrlRef = useRef(null);

    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [lang, setLang] = useState(LANG_OPTIONS[2]);
    const [outputText, setOutputText] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [confidence, setConfidence] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);

    const setPreview = useCallback((nextFile) => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        if (!nextFile) {
            previewUrlRef.current = null;
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(nextFile);
        previewUrlRef.current = url;
        setPreviewUrl(url);
    }, []);

    const addFile = useCallback((incoming) => {
        const picked = Array.from(incoming || [])[0];
        if (!picked) return;

        if (!isSupportedImageToTextFile(picked)) {
            const msg = t('imageToText.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        if (picked.size > MAX_FILE_BYTES) {
            const msg = t('imageToText.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(picked);
        setPreview(picked);
        setOutputText('');
        setProgress(0);
        setConfidence(null);
        setError(null);
    }, [setPreview, t]);

    useImagePaste((pasted) => addFile(pasted));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setPreview(null);
        setOutputText('');
        setProgress(0);
        setConfidence(null);
        setError(null);
    };

    const handleExtract = async () => {
        if (!file || processing) return;

        recordToolUsage('text/image-to-text', { category: 'text' });

        setProcessing(true);
        setOutputText('');
        setProgress(0);
        setConfidence(null);
        setError(null);

        try {
            const { text, confidence: score } = await extractTextFromImage(
                file,
                lang,
                ({ progress: pct }) => setProgress(pct),
            );
            const nextText = text || t('imageToText.noText');
            setOutputText(nextText);
            setConfidence(text ? score : null);
            setProgress(100);
            notifications.show({
                title: t('common.success'),
                message: t('imageToText.done'),
                color: 'green',
            });
        } catch {
            const msg = t('imageToText.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handleCopy = () => {
        if (!outputText) return;

        if (copyTextToClipboard(outputText)) {
            notifications.show({
                title: t('common.success'),
                message: t('imageToText.copied'),
                color: 'green',
            });
            return;
        }

        void navigator.clipboard?.writeText(outputText)
            .then(() => {
                notifications.show({
                    title: t('common.success'),
                    message: t('imageToText.copied'),
                    color: 'green',
                });
            })
            .catch(() => {
                notifications.show({
                    title: t('common.error'),
                    message: t('imageToText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const hasResult = outputText.length > 0;
    const canExtract = !!file && !processing;

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: canExtract,
        processing,
        hasResult,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`imageToText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('imageToText.statusReady'),
        success: t('imageToText.done'),
        error: error || t('toolPage.statusText.error'),
    });

    const processingLabel = progress > 0
        ? t('imageToText.processingProgress', { progress: toBnDigits(progress) })
        : t('imageToText.processing');

    return (
        <ToolPageLayout
            slug="text/image-to-text"
            subtitleKey="imageToText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            howToSteps={howToSteps}
            onReset={file || outputText || error ? handleReset : undefined}
            primaryAction={{
                label: t('imageToText.extract'),
                onClick: handleExtract,
                disabled: !canExtract,
                loading: processing,
                icon: <IconScan size={18} stroke={1.9} />,
            }}
            controls={(
                <ToolBoothTabs onPick={(picked) => addFile([picked])}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <ImageDropZone
                            title={t('imageToText.dropTitle')}
                            hint={t('imageToText.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <ImageFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={processing ? undefined : handleReset}
                            removeLabel={t('imageToText.removeFile')}
                        />
                    )}
                </ToolBoothTabs>
            )}
            settings={(
                <div className="space-y-3">
                {file ? (
                <ToolCard title={t('imageToText.languageTitle')}>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {LANG_OPTIONS.map((option) => (
                            <button
                                key={option}
                                type="button"
                                disabled={processing}
                                onClick={() => {
                                    setLang(option);
                                    setOutputText('');
                                    setError(null);
                                }}
                                className={langButtonClass(lang === option)}
                            >
                                {t(`imageToText.languages.${option}`)}
                            </button>
                        ))}
                    </div>
                    <p className="mt-2 text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                        {t('imageToText.languageHint')}
                    </p>
                </ToolCard>
                ) : null}
                {/* Last in the column, on its own: it leaves this tool rather
                    than changing it, so it does not belong among the settings. */}
                <AiToolLink
                    to="/tools/text/ai-image-to-text"
                    titleKey="imageToText.aiLink.title"
                    bodyKey="imageToText.aiLink.body"
                />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={hasResult ? t('imageToText.outputTitle') : t('imageToText.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('imageToText.previewEmpty')}
                    idleHint={t('imageToText.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress}
                    error={error}
                    showIdle={!file && !error}
                    flushContent={hasResult}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('imageToText.extract')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {hasResult ? (
                        <div className="flex w-full flex-col gap-2.5">
                            <div className="flex items-center justify-end">
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    disabled={!outputText}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-sky-400/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    <IconCopy size={14} stroke={1.9} />
                                    {t('common.copy')}
                                </button>
                            </div>
                            <textarea
                                value={outputText}
                                onChange={(e) => setOutputText(e.target.value)}
                                className={textareaClass}
                            />
                            {/* What the engine thought of its own reading. A low
                                number is the honest answer to "why is this
                                wrong" — the picture, not the text, is what
                                needs fixing. */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                {confidence !== null ? (
                                    <span className={`text-[11.5px] font-semibold ${
                                        confidence >= 80
                                            ? 'text-[#1E9E5E] dark:text-emerald-400'
                                            : confidence >= 60
                                                ? 'text-[#B7791F] dark:text-amber-400'
                                                : 'text-[#D33A2C] dark:text-red-400'
                                    }`}>
                                        {t('imageToText.accuracy', { score: toBnDigits(confidence) })}
                                    </span>
                                ) : <span />}
                                <p className="text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('imageToText.characterCount', { count: toBnDigits(outputText.length) })}
                                </p>
                            </div>
                            {/* Under ~40 the engine is not reading the page at
                                all — almost always handwriting, which the
                                printed-type model cannot do. Saying so beats
                                leaving someone to wonder why the text is
                                nonsense. */}
                            {confidence !== null && confidence < 40 ? (
                                <p className="rounded-lg bg-[#FDECEA] px-3 py-2 text-[11.5px] leading-relaxed text-[#9C3328] dark:bg-red-950/20 dark:text-red-300">
                                    {t('imageToText.unreadableHint')}
                                </p>
                            ) : confidence !== null && confidence < 70 ? (
                                <p className="rounded-lg bg-[#FDF6EC] px-3 py-2 text-[11.5px] leading-relaxed text-[#8A6A2F] dark:bg-amber-950/20 dark:text-amber-300">
                                    {t('imageToText.lowAccuracyHint')}
                                </p>
                            ) : null}
                        </div>
                    ) : file && previewUrl ? (
                        <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-2">
                            <img
                                src={previewUrl}
                                alt={file.name}
                                className="max-h-[min(420px,60vh)] max-w-full rounded-xl border border-[#ECE8E4] object-contain shadow-sm dark:border-zinc-700"
                            />
                            <p className="text-center text-[12px] text-[#A8A099] dark:text-zinc-500">
                                {file.name}
                            </p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
